/**
 * Client-controlled chat archiving ("archive mode").
 *
 * When the room owner enables archive mode, participants who must keep
 * records (banks, law firms) can save the conversation as an archive that is
 * encrypted CLIENT-SIDE with the room's v2 chat key before it leaves the
 * browser. The server / service provider never sees readable content — the
 * archive can only be opened by someone holding the room key.
 */
/**
 * Feature flag: the archive-mode UI (badges, banners, save/open buttons) is
 * hidden for now — the feature is unfinished/confusing for users. Flip to
 * true to bring the whole archive UI back; the underlying code is untouched.
 */
export const ARCHIVE_FEATURE_ENABLED = false;
/**
 * Builds the encrypted archive object. `encryptFn` must be the room's v2
 * encryptor (throws while the confirmed key is not held).
 */
export async function buildEncryptedArchive(room, messages, encryptFn) {
    const plain = JSON.stringify(messages
        .filter((m) => !m.failedDecrypt)
        .map(({ id, text, deviceId, timestamp, isMine }) => ({ id, text, deviceId, timestamp, isMine })));
    const encryptedPayload = await encryptFn(plain);
    return {
        format: 'dwinity-chat-archive',
        version: 1,
        room,
        createdAt: new Date().toISOString(),
        messageCount: messages.filter((m) => !m.failedDecrypt).length,
        encryptedPayload,
    };
}
/**
 * Reading an archive can fail for user-explainable reasons; `code`
 * distinguishes them so the UI can show a translated message.
 */
export class ArchiveReadError extends Error {
    code;
    constructor(code, message) {
        super(message || code);
        this.code = code;
        this.name = 'ArchiveReadError';
    }
}
/**
 * Maps an archive-open failure to the translation key both the desktop and
 * mobile openers show, so the two UIs cannot drift apart.
 */
export function archiveOpenErrorKey(err, keyReady) {
    if (err instanceof ArchiveReadError && err.code === 'invalid_format') {
        return 'mobile.chat.archive.open_invalid';
    }
    if (err instanceof ArchiveReadError && err.code === 'unsupported_version') {
        return 'mobile.chat.archive.open_unsupported_version';
    }
    if (!keyReady) {
        return 'mobile.chat.archive.open_no_key';
    }
    return 'mobile.chat.archive.open_decrypt_failed';
}
/** Parses raw file text and validates it is a dwinity chat archive. */
export function parseArchiveJson(text) {
    let data;
    try {
        data = JSON.parse(text);
    }
    catch {
        throw new ArchiveReadError('invalid_format', 'not valid JSON');
    }
    const a = data;
    if (!a || typeof a !== 'object'
        || a.format !== 'dwinity-chat-archive'
        || typeof a.encryptedPayload !== 'string' || !a.encryptedPayload
        || typeof a.room !== 'string') {
        throw new ArchiveReadError('invalid_format', 'not a dwinity-chat-archive file');
    }
    // A future client may write a newer archive format this client cannot
    // decrypt correctly. Fail with a dedicated code so the UI can say
    // "update the app" instead of a misleading decryption error.
    if (a.version !== 1) {
        throw new ArchiveReadError('unsupported_version', `archive version ${String(a.version)} is newer than this client supports`);
    }
    return a;
}
/**
 * Decrypts the archive with the room's v2 key (`decryptFn` from
 * useChatCrypto). Throws ArchiveReadError('decrypt_failed') when the key is
 * missing/wrong or the payload is corrupted.
 */
export async function decryptArchive(archive, decryptFn) {
    let plain;
    try {
        plain = await decryptFn(archive.encryptedPayload);
    }
    catch (err) {
        throw new ArchiveReadError('decrypt_failed', err instanceof Error ? err.message : String(err));
    }
    let parsed;
    try {
        parsed = JSON.parse(plain);
    }
    catch {
        throw new ArchiveReadError('decrypt_failed', 'decrypted payload is not JSON');
    }
    if (!Array.isArray(parsed)) {
        throw new ArchiveReadError('decrypt_failed', 'decrypted payload is not a message list');
    }
    return parsed
        .filter((m) => !!m && typeof m === 'object'
        && typeof m.text === 'string'
        && typeof m.deviceId === 'string')
        .map((m, i) => ({
        id: typeof m.id === 'string' ? m.id : `archived-${i}`,
        text: m.text,
        deviceId: m.deviceId,
        timestamp: typeof m.timestamp === 'number' ? m.timestamp : 0,
        isMine: !!m.isMine,
    }));
}
export function archiveFileName(room) {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    return `chat-archive-${room.slice(0, 10)}-${stamp}.json`;
}
/** Triggers a browser download of the encrypted archive (JSON file). */
export function downloadArchive(archive) {
    const blob = new Blob([JSON.stringify(archive, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = archiveFileName(archive.room);
    a.click();
    URL.revokeObjectURL(url);
}
/** Wraps the archive as a File for uploading to the user's own storage. */
export function archiveAsFile(archive) {
    return new File([JSON.stringify(archive, null, 2)], archiveFileName(archive.room), { type: 'application/json' });
}
